//REV-21369 — pdf.js als LAUFZEIT-ASSET (ADR-0021 E2).
//
//⚠️ webpack in Angular 14 parst pdf.js NIE. Die Bibliothek liegt per Asset-Glob unter
//`assets/pdfjs/<version>/` (angular.json, beide Targets) und wird mit `webpackIgnore` nativ als
//ES-Modul geladen. Damit entfallen ES2022-Syntax, `import.meta.url` und der Node-Mindeststand zur
//Buildzeit — und wir bekommen die aktuelle Hauptversion samt CVE-2024-4367-Fix ohne Upgrade.
//
//⚠️ Die Version steht DREIMAL: hier, im Asset-Glob (`output`) und in `package.json`. Ein Test
//(`src/tests/pdf-viewer.unit.spec.ts`, Versions-Abgleich) hält die drei zusammen — ein Update, das nur eine Stelle ändert,
//lädt sonst eine Datei, die es nicht gibt, und der Viewer bleibt leer.

/** Exakt die Version aus `package.json` — siehe Kopfkommentar. */
export const PDFJS_VERSION = '6.3.289';

/**
 * Die schmale Schnittstelle, die der Viewer von pdf.js benutzt. Bewusst eigene Typen statt der
 * `.d.ts` von `pdfjs-dist`: TypeScript 4.8 liest sie nicht zuverlässig (ADR-0021 E2).
 */
export interface PdfJsViewport {
    width: number;
    height: number;
    rotation: number;
    convertToPdfPoint(x: number, y: number): number[];
    convertToViewportPoint(x: number, y: number): number[];
}

export interface PdfJsPage {
    view: [number, number, number, number];
    rotate: number;
    getViewport(params: { scale: number }): PdfJsViewport;
    render(params: { canvas: HTMLCanvasElement; viewport: PdfJsViewport; transform?: number[] }): {
        promise: Promise<void>;
        cancel(): void;
    };
    getTextContent(): Promise<unknown>;
    cleanup(): void;
}

/**
 * ⚠️ KEIN `destroy()`: in pdf.js 6 hat nur der LADE-TASK (`getDocument(...)`) ein `destroy()`, nicht
 * das Dokument. Ein `doc.destroy()` warf beim Verlassen der Seite in `ngOnDestroy` — und ein Wurf dort
 * bricht die Navigation des Routers ab: die nächste Seite blieb leer.
 */
export interface PdfJsDocument {
    numPages: number;
    getPage(pageNumber: number): Promise<PdfJsPage>;
}

/** Der Lade-Task — er besitzt den Worker-Transport und ist das, was man zerstört. */
export interface PdfJsLoadingTask {
    promise: Promise<PdfJsDocument>;
    destroy(): Promise<void>;
}

export interface PdfJsTextLayer {
    render(): Promise<void>;
    cancel(): void;
}

export interface PdfJsLib {
    version: string;
    GlobalWorkerOptions: { workerSrc: string };
    getDocument(params: Record<string, unknown>): PdfJsLoadingTask;
    TextLayer: new (params: { textContentSource: unknown; container: HTMLElement; viewport: PdfJsViewport }) => PdfJsTextLayer;
}

/** `assets/pdfjs/<version>/` als ABSOLUTE URL — ein relativer Import löste gegen den Chunk auf, nicht gegen die Seite. */
export function pdfjsAssetRoot(baseUri: string = currentBaseUri()): string {
    return new URL(`assets/pdfjs/${PDFJS_VERSION}/`, baseUri).href;
}

/** `document.baseURI` im Browser; ausserhalb (Node-Unit-Tests) ein fester Platzhalter. */
function currentBaseUri(): string {
    return typeof document === 'undefined' ? 'http://localhost/' : document.baseURI;
}

/**
 * ⚠️ zone.js 0.11 ersetzt `window.Promise` durch `ZoneAwarePromise`, und das kennt
 * `Promise.withResolvers` nicht — auch in einem Browser, der es nativ hat. pdf.js ruft es auf dem
 * Hauptthread, ohne es rendert KEIN PDF (Spike-Befund 1). Ein Upgrade von zone.js geht nicht:
 * Angular 14 verlangt `~0.11.4 || ~0.12.0`.
 *
 * Hier statt in `polyfills.ts`, weil es genau einen Grund hat und der hier steht: es wird gesetzt,
 * bevor pdf.js zum ersten Mal geladen wird, in jeder App, die den Viewer benutzt. `Promise` bleibt
 * `ZoneAwarePromise`, die Change Detection läuft also weiter. Entfällt mit zone.js ≥ 0.14 / zoneless.
 */
export function ensurePromiseWithResolvers(target: PromiseConstructor = Promise): void {
    const ctor = target as PromiseConstructor & { withResolvers?: unknown };
    if (typeof ctor.withResolvers === 'function') {
        return;
    }
    Object.defineProperty(ctor, 'withResolvers', {
        configurable: true,
        writable: true,
        value: function withResolvers<T>(this: PromiseConstructor) {
            let resolve!: (value: T | PromiseLike<T>) => void;
            let reject!: (reason?: unknown) => void;
            const promise = new this<T>((res, rej) => {
                resolve = res;
                reject = rej;
            });
            return { promise, resolve, reject };
        },
    });
}

/**
 * Wie ein ES-Modul geladen wird. Eigene Schnittstelle, damit die Rückfall-Logik ohne Browser prüfbar
 * ist (`pdf-viewer.unit.spec.ts`); im Browser sind es `import()`, `fetch` und Blob-URLs.
 */
export interface ModuleLoaderDeps {
    importModule(url: string): Promise<unknown>;
    fetchText(url: string): Promise<string>;
    createScriptUrl(source: string): string;
    revokeUrl(url: string): void;
}

/**
 * ⚠️ Ein Modul lädt der Browser NUR, wenn der Server es als JavaScript ausweist. `.mjs` kennen nicht
 * alle Server: testing lieferte `pdf.min.mjs` als `application/octet-stream`, und der Viewer blieb
 * mit „Failed to fetch dynamically imported module" stehen (REV-21369, 12.10.2026). Unser
 * `nginx-spa.conf` setzt den Typ — aber was vor oder statt ihm ausliefert, liegt nicht in diesem Repo.
 *
 * Darum: erst normal importieren (richtiger Server ⇒ nichts ändert sich); schlägt das fehl, die Datei
 * selbst holen und als Blob mit `text/javascript` importieren. `fromBlob` sagt dem Aufrufer, dass der
 * Server den Typ nicht stimmt — der Worker braucht dann denselben Weg.
 */
export async function importModuleTolerant<T>(url: string, deps: ModuleLoaderDeps): Promise<{ module: T; fromBlob: boolean }> {
    try {
        return { module: (await deps.importModule(url)) as T, fromBlob: false };
    } catch (direct) {
        let source: string;
        try {
            source = await deps.fetchText(url);
        } catch {
            // Die Datei ist gar nicht zu holen: der ursprüngliche Fehler sagt mehr als der zweite.
            throw direct;
        }
        const blobUrl = deps.createScriptUrl(source);
        try {
            return { module: (await deps.importModule(blobUrl)) as T, fromBlob: true };
        } finally {
            // Ein ausgewertetes Modul braucht seine URL nicht mehr.
            deps.revokeUrl(blobUrl);
        }
    }
}

/** Der Worker als Blob-URL `text/javascript` — nur, wenn der Server `.mjs` falsch ausweist (siehe oben). */
export async function workerScriptUrl(url: string, fromBlob: boolean, deps: ModuleLoaderDeps): Promise<string> {
    // ⚠️ Diese Blob-URL wird NICHT freigegeben: pdf.js startet je Beleg einen Worker aus ihr.
    return fromBlob ? deps.createScriptUrl(await deps.fetchText(url)) : url;
}

const browserLoaderDeps: ModuleLoaderDeps = {
    importModule: (url) => import(/* webpackIgnore: true */ url),
    fetchText: async (url) => {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) {
            throw new Error(`pdf.js asset ${url}: HTTP ${response.status}`);
        }
        return response.text();
    },
    createScriptUrl: (source) => URL.createObjectURL(new Blob([source], { type: 'text/javascript' })),
    revokeUrl: (url) => URL.revokeObjectURL(url),
};

let loading: Promise<PdfJsLib> | null = null;

/** Lädt pdf.js einmal je Seite und setzt den Worker auf die Nachbardatei derselben Herkunft. */
export function loadPdfJs(): Promise<PdfJsLib> {
    if (loading === null) {
        ensurePromiseWithResolvers();
        const build = `${pdfjsAssetRoot()}build/`;
        loading = importModuleTolerant<PdfJsLib>(`${build}pdf.min.mjs`, browserLoaderDeps).then(async ({ module: lib, fromBlob }) => {
            lib.GlobalWorkerOptions.workerSrc = await workerScriptUrl(`${build}pdf.worker.min.mjs`, fromBlob, browserLoaderDeps);
            return lib;
        });
        // Ein fehlgeschlagener Versuch darf den nächsten nicht vergiften (z. B. kurz offline).
        loading.catch(() => {
            loading = null;
        });
    }
    return loading;
}

/**
 * Die Optionen für `getDocument` — EINE Stelle, damit die Härtung nicht je Aufruf vergessen wird.
 *
 * ⚠️ Fremd-PDFs (ADR-0021 E7): kein XFA, keine Scripting-Sandbox (die Datei wird gar nicht erst
 * ausgeliefert), keine AnnotationLayer (Links, Formulare) — die rendert der Viewer nicht.
 * `isEvalSupported` gibt es in pdf.js 6 nicht mehr: der Build enthält kein `eval` und kein
 * `new Function`; die Härtung ist die gepinnte Version.
 */
export function documentOptions(data: ArrayBuffer): Record<string, unknown> {
    const root = pdfjsAssetRoot();
    return {
        // ⚠️ Eine KOPIE: pdf.js überträgt den Puffer an den Worker und macht ihn damit im
        // Hauptthread unbrauchbar. Ohne Kopie wäre ein zweites Öffnen desselben Belegs leer.
        data: new Uint8Array(data.slice(0)),
        enableXfa: false,
        cMapUrl: `${root}cmaps/`,
        cMapPacked: true,
        standardFontDataUrl: `${root}standard_fonts/`,
        wasmUrl: `${root}wasm/`,
        iccUrl: `${root}iccs/`,
    };
}
